import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { ROUTE_STRING } from 'constants/RouteString';
import PipelineFlow from './PipelineFlow';
import RunHistoryStats from './RunHistoryStats';
import RunTriggeredBy from './RunTriggeredBy';

// AC-1/AC-4/AC-5/AC-9/AC-11 — the right-hand (desktop) / first-in-reading-order (stacked)
// "run console": the 2-stage timeline + run-history stats + the existing "View run
// history" link-out. Every state below is preserved from the pre-redesign page: the
// initial-loading window (never a false "no runs yet"), the runs-fetch-error alert, and
// the genuine "no runs yet" empty state — which renders NEITHER the timeline NOR the
// stats strip (AC-9/AC-19).
const RunConsole = ({ pipelineId, hasAnyRun, isLoadingRuns, isRunsError, run, elapsedLabel, runs }) => (
	<Box display="flex" flexDirection="column" gap={3}>
		<Box display="flex" alignItems="baseline" justifyContent="space-between">
			<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
				Run console
			</Typography>
			<Button component={RouterLink} to={ROUTE_STRING.PIPELINE.RUNS.replace(':id', pipelineId)} size="small">
				View run history
			</Button>
		</Box>

		{isLoadingRuns ? (
			// The runs query resolves independently of the pipeline GET — don't assert
			// "no runs yet" before it has settled.
			<Skeleton variant="rounded" height={220} />
		) : isRunsError ? (
			<Alert severity="warning">Could not load this pipeline&apos;s run history.</Alert>
		) : !hasAnyRun ? (
			// AC-9 — a pipeline that has never run: explicit empty state, no timeline, no stats.
			<Alert severity="info">This pipeline has no runs yet.</Alert>
		) : (
			<>
				{/* deploy-pipelines-run-provenance (Wave 2) — the actor line joins the outcome block,
				    above the stage timeline. It reads the run object this console already receives; no
				    new prop is threaded down from PipelineDetail and no new request is issued (the fact
				    rides the GET /runs payload the page already polls — NFR-Performance, and Wave 1's
				    AC-17 single-poller invariant is untouched). */}
				<RunTriggeredBy run={run} />
				{/* deploy-pipelines-run-logs (Wave 3) — `pipelineId` is threaded on so the Build
				    stage can render its log panel. It is the id this console ALREADY receives for
				    the "View run history" link; no new page-level state and no new page-level
				    request are introduced (the panel owns its own log fetch, and Wave 1's AC-17
				    single-run-poller invariant is untouched). */}
				<PipelineFlow pipelineId={pipelineId} run={run} elapsedLabel={elapsedLabel} />
				<RunHistoryStats runs={runs} />
			</>
		)}
	</Box>
);

export default React.memo(RunConsole);

RunConsole.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	hasAnyRun: PropTypes.bool,
	isLoadingRuns: PropTypes.bool,
	isRunsError: PropTypes.bool,
	run: PropTypes.object,
	elapsedLabel: PropTypes.string,
	runs: PropTypes.array
};
